import { useMemo } from 'react';
import {useQuery } from '@common-fe/common-fe';

import { api } from '@/api';
import { API_URL, IEligibleServicesSearchResponse } from '@/dtos';
import { AccountOrder } from '@/modules/UseMyMoney/queries';
import { formatAccountOrder } from '@/modules/UseMyMoney/utils';
import useEmployeeStore from '@/modules/user/stores/useEmployee.store';

interface Props {
  dependentId?: string;
  dateOfService?: string;
  searchString?: string;
}

const useGetAiSuggestedServicesQuery = ({ dependentId, dateOfService, searchString }: Props) => {
  const { employee } = useEmployeeStore();

  const { data, isLoading } = useQuery(
    [API_URL.AI_SEARCH_SERVICES_ELIGIBLE, employee?.id, dependentId, dateOfService, searchString],
    () => api.get<IEligibleServicesSearchResponse>(API_URL.AI_SEARCH_SERVICES_ELIGIBLE, {
      params: {
        employee_id: employee?.id || undefined,
        dependent_id: dependentId || undefined,
        service_on: dateOfService || undefined,
        search_string: searchString || undefined,
      },
    }),
    { enabled: Boolean(dateOfService && employee?.id && searchString && searchString?.length >= 3) },
  );

  const aiSuggestedAccountOrdersArrays = useMemo((): AccountOrder[][] => {
    const accountOrdersArrays = data?.data?.account_orders;

    if (!accountOrdersArrays?.length) return [];

    const availableAccountOrdersArrays = accountOrdersArrays
      ?.map((accountOrders) => accountOrders?.filter((accountOrder) => !accountOrder?.is_forfeited));

    if (!availableAccountOrdersArrays?.length) return [];

    const sortedAccountOrdersArrays = availableAccountOrdersArrays
      ?.map((accountOrders) => accountOrders?.sort((prev, next) => ((prev?.order || 0) > (next?.order || 0) ? 1 : -1)));

    const accountOrdersArraysWithUniqServices = sortedAccountOrdersArrays?.map((accountOrders, index) => {
      const seenServiceIds = new Set<string>();

      return accountOrders.map((accountOrder) => {
        const formatted = formatAccountOrder(accountOrder, index);
        const uniqueServices = (formatted.services || []).filter((service) => {
          if (seenServiceIds.has(service.id)) return false;
          seenServiceIds.add(service.id);
          return true;
        });

        return { ...formatted, services: uniqueServices };
      });
    });

    return accountOrdersArraysWithUniqServices;
  }, [data]);

  return {
    aiSuggestedAccountOrdersArrays,
    isAiSuggestedAccountOrdersLoading: isLoading,
  };
};

export default useGetAiSuggestedServicesQuery;
